import { useCallback, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import moment from 'moment';
import { useLangContext } from '../../providers/LangProvider/LangProviderContext.js';
import amountFormat from '../../utils/amountFormat.js';
import { BALANCE_LOGS_PAGE_SIZE, getPartnerBalanceLogs } from './balanceLogsApi.js';
import {
    BalanceLogsLoadMoreStyled,
    BalanceLogsPageStyled,
    BalanceLogsSectionStyled,
    BalanceLogsTableStyled,
    BalanceLogsTableWrapStyled,
} from './styled/BalanceLogsStyled';

type BalanceLogRow = {
    id: number;
    created_at: string;
    type?: string | null;
    system?: string | null;
    reason?: string | null;
    amount: number | string;
    balance: number | string;
};

function formatAmountClass(amount: number | string): string {
    const value = parseFloat(String(amount));
    if (!Number.isFinite(value) || value === 0) {
        return 'col-amount';
    }
    return value > 0 ? 'col-amount positive' : 'col-amount negative';
}

function BalanceLogs() {
    const { getLangPath } = useLangContext();
    const [rows, setRows] = useState<BalanceLogRow[]>([]);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);

    const loadLogs = useCallback((offset = 0, append = false) => {
        setLoading(true);
        getPartnerBalanceLogs({ limit: BALANCE_LOGS_PAGE_SIZE, offset })
            .then((res: BalanceLogRow[]) => {
                const items = Array.isArray(res) ? res : [];
                setRows((prev) => (append ? [...prev, ...items] : items));
                setHasMore(items.length >= BALANCE_LOGS_PAGE_SIZE);
            })
            .catch(() => {
                if (!append) {
                    setRows([]);
                    setHasMore(false);
                }
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        loadLogs(0, false);
    }, [loadLogs]);

    return (
        <BalanceLogsPageStyled>
            <div className="page-header">
                <span className="section-eyebrow">{getLangPath('balance_logs.eyebrow')}</span>
                <h1 className="page-title">{getLangPath('balance_logs.title')}</h1>
                <p className="page-desc">{getLangPath('balance_logs.desc')}</p>
            </div>

            <BalanceLogsSectionStyled>
                <div className="section-header">
                    <div className="section-heading">
                        <h2 className="section-title">{getLangPath('balance_logs.section_title')}</h2>
                        <p className="section-desc">{getLangPath('balance_logs.section_desc')}</p>
                    </div>
                </div>

                <BalanceLogsTableWrapStyled>
                    <BalanceLogsTableStyled>
                        <thead>
                            <tr>
                                <th>{getLangPath('balance_logs.columns.date')}</th>
                                <th>{getLangPath('balance_logs.columns.type')}</th>
                                <th>{getLangPath('balance_logs.columns.system')}</th>
                                <th>{getLangPath('balance_logs.columns.reason')}</th>
                                <th className="col-amount">{getLangPath('balance_logs.columns.amount')}</th>
                                <th className="col-amount">{getLangPath('balance_logs.columns.balance')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((item) => (
                                <tr key={item.id}>
                                    <td className="col-muted">
                                        {moment(item.created_at).format('YYYY-MM-DD HH:mm')}
                                    </td>
                                    <td>{item.type || '—'}</td>
                                    <td className="col-muted">{item.system || '—'}</td>
                                    <td className="col-reason">{item.reason || '—'}</td>
                                    <td className={formatAmountClass(item.amount)}>
                                        ${amountFormat(Number(item.amount))}
                                    </td>
                                    <td className="col-amount">${amountFormat(Number(item.balance))}</td>
                                </tr>
                            ))}
                            {!rows.length && !loading && (
                                <tr>
                                    <td className="empty-cell" colSpan={6}>
                                        {getLangPath('balance_logs.empty')}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </BalanceLogsTableStyled>
                </BalanceLogsTableWrapStyled>

                {hasMore && (
                    <BalanceLogsLoadMoreStyled>
                        <Button
                            variant="secondary"
                            disabled={loading}
                            onClick={() => loadLogs(rows.length, true)}
                        >
                            {loading
                                ? getLangPath('balance_logs.loading')
                                : getLangPath('balance_logs.show_more')}
                        </Button>
                    </BalanceLogsLoadMoreStyled>
                )}
            </BalanceLogsSectionStyled>
        </BalanceLogsPageStyled>
    );
}

export default BalanceLogs;
